// ===========================================
// HOOK: Notificaciones genéricas por módulo (CORE)
// ===========================================
/**
 * Helper reutilizable para disparar notificaciones in-app en cualquier módulo
 * de BrainHub (Finanzas, Fiscal, Comercial, Operacional, etc.).
 *
 * Creado en ORR-56 Fase 1 del Epic ORR-55 para centralizar el patrón de:
 * 1. Calcular destinatarios según contexto (org, cliente, rol)
 * 2. Filtrar actor ≠ destinatario
 * 3. Llamar al servicio canónico `notificacionesService`
 * 4. Best-effort: errores se loggean pero no rompen la mutación principal
 *
 * Uso típico en hooks de mutation:
 *   const { notificar, destinatariosOrg, responsablesCliente } = useNotificacionesModulo()
 *
 *   return useMutation({
 *     onSuccess: async (resultado) => {
 *       const emails = await destinatariosOrg(organizacionId)
 *       await notificar({
 *         tipo: 'alerta',
 *         titulo: 'Nuevo pago registrado',
 *         mensaje: `${actor} registró un pago de $${monto} de ${cliente}`,
 *         enlace: `/movimientos?id=${resultado.id}`,
 *         destinatarios: emails,
 *       })
 *     },
 *   })
 *
 * Referencia de patrón: `src/core/hooks/useNotificacionesTarea.ts` (ORR-54)
 */

import { useCallback, useMemo } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useSupabase } from '@/lib/supabase-context'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { notificacionesService } from '../services/notificacionesService'
import type { TipoNotificacion } from '../types/notificaciones'
import type { Permisos, RolMultiTenant } from '@/types/roles'

interface NotificarParams {
  tipo: TipoNotificacion
  titulo: string
  mensaje: string
  enlace?: string
  /** Lista de emails destinatarios. El actor (user.email) se excluye automáticamente. */
  destinatarios: string[]
  /**
   * ORR-613: flag que hace falta para ver el contenido. Viene de la plantilla cuando el mensaje
   * lleva importes. Los destinatarios sin el flag en la organización activa se descartan.
   */
  permiso?: keyof Permisos
}

export function useNotificacionesModulo() {
  const { supabase } = useSupabase()
  const { user } = useAuth0()
  const { organizacionId } = useOrganizacion()
  const userEmail = user?.email

  /**
   * Dispara una notificación a una lista de destinatarios, excluyendo al actor.
   * Best-effort: los errores se loggean pero no rompen el flujo.
   */
  const notificar = useCallback(
    async (params: NotificarParams) => {
      if (!supabase || !userEmail) return

      // Deduplicar y excluir actor
      const candidatos = Array.from(new Set(params.destinatarios)).filter(
        (email) => email && email !== userEmail
      )
      if (candidatos.length === 0) return
      // Sin organización activa no hay dónde evaluar el permiso: no sale el aviso.
      if (params.permiso && !organizacionId) return

      try {
        const destinos = params.permiso && organizacionId
          ? await notificacionesService.filtrarPorPermiso(supabase, organizacionId, candidatos, params.permiso)
          : candidatos
        if (destinos.length === 0) return

        // La organización va en origen para que el trigger de ORR-613 evalúe ahí el permiso. Sin
        // actor_email: la policy lo compara con el email del caller y un desfase rechazaría el insert.
        await notificacionesService.crearParaMultiples(supabase, destinos, {
          tipo: params.tipo,
          titulo: params.titulo,
          mensaje: params.mensaje,
          enlace: params.enlace ?? null,
        }, organizacionId ? { canal: 'web', organizacion_id: organizacionId } : undefined)
      } catch (err) {
        console.error('[notificaciones] best-effort error:', err)
      }
    },
    [supabase, userEmail, organizacionId]
  )

  /**
   * Obtiene los emails de los usuarios activos de una organización.
   * Útil para eventos que deben llegar a todo el equipo (ej: nuevo pago registrado)
   * o a un subconjunto filtrado por rol (ej: solo owner/admin).
   *
   * @param organizacionId UUID de la organización
   * @param rolFilter opcional: filtrar por roles específicos (ej: ['owner', 'admin'])
   * @param permiso opcional: solo quien tiene ese permiso efectivo (ej: 'verMovimientos')
   * @returns array de emails (excluyendo al actor)
   */
  const destinatariosOrg = useCallback(
    async (organizacionId: string, rolFilter?: RolMultiTenant[], permiso?: keyof Permisos): Promise<string[]> => {
      if (!supabase) return []
      return notificacionesService.obtenerDestinatariosOrg(
        supabase,
        organizacionId,
        userEmail,
        rolFilter,
        permiso,
      )
    },
    [supabase, userEmail]
  )

  /**
   * Obtiene los emails de los responsables de un cliente.
   * Si el cliente no tiene responsables asignados, cae al fallback: todos los admins/owner de la org.
   * Con `permiso`, el fallback corre también cuando ningún responsable tiene el flag.
   *
   * @param clienteId UUID del cliente
   * @param permiso flag que hace falta para ver el aviso (ORR-613)
   * @returns array de emails (excluyendo al actor)
   */
  const responsablesCliente = useCallback(
    async (clienteId: string, permiso?: keyof Permisos): Promise<string[]> => {
      if (!supabase) return []
      return notificacionesService.obtenerResponsablesCliente(supabase, clienteId, userEmail, permiso)
    },
    [supabase, userEmail]
  )

  /**
   * Obtiene el nombre displayable de un cliente (via JOIN a personas).
   * Evita el bug común de queryear `.from('clientes').select('nombre')` —
   * ver `notificacionesService.obtenerNombreCliente` para contexto.
   *
   * @param clienteId UUID del cliente
   * @returns nombre computado o 'Cliente sin nombre' si no se encuentra
   */
  const nombreCliente = useCallback(
    async (clienteId: string): Promise<string> => {
      if (!supabase) return 'Cliente sin nombre'
      return notificacionesService.obtenerNombreCliente(supabase, clienteId)
    },
    [supabase]
  )

  // Memoizar el objeto retornado para que mantenga referencia estable entre
  // renders mientras las deps (supabase, userEmail) no cambien. Esto evita
  // que los consumidores que pasen el objeto a useEffect/useMemo disparen
  // efectos infinitos por referencia nueva en cada render.
  return useMemo(
    () => ({
      notificar,
      destinatariosOrg,
      responsablesCliente,
      nombreCliente,
    }),
    [notificar, destinatariosOrg, responsablesCliente, nombreCliente],
  )
}
